import React from 'react';

import { isValid as isDateValid } from 'date-fns';

// Material-UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
//import TextField, { TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders,
  SignupResultStatus
} from '../../interfaces';

import {
  MemberSignup
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  NULL_DATETIME_STRING,
  NULL_ID,
  SignupExceptionEnum
} from '../../global';

// UTC to local date formatting
import { useDates } from '../../hooks/UseDates';

// Signup date criteria
import { useSignupDates } from '../../hooks/UseSignupDates';

// Auth0 user hooks
import { useAuth0User } from '../../hooks/UseAuth0User';

interface MemberRequestEditProps {
  handleClose: () => void;
  handleUpdate: (signup: MemberSignup) => void;
  memberSignup: MemberSignup;
}

const MemberRequestEdit: React.FC<MemberRequestEditProps> = (props) => {

  const { enqueueSnackbar } = useSnackbar();
  const { calcMaxArrival, calcMaxDeparture, calcMinArrival, calcMinDeparture } = useSignupDates();
  const { dateFormatted, dateStringToLocalDateFormatted } = useDates();
  const { getAccessToken } = useAuth0User();

  const [enableSave, setEnableSave] = React.useState(false);
  const [memberSignup, setMemberSignup] = React.useState<MemberSignup>(new MemberSignup());
  const [maxArrival, setMaxArrival] = React.useState<string>(EMPTY_STRING);
  const [maxDeparture, setMaxDeparture] = React.useState<string>(EMPTY_STRING);
  const [minArrival, setMinArrival] = React.useState<string>(EMPTY_STRING);
  const [minDeparture, setMinDeparture] = React.useState<string>(EMPTY_STRING);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(1)
    },
    actions: {
      margin: theme.spacing(3, 0, 2, 0)
    },
    entryField: {
      margin: theme.spacing(2, 0, 2, 0)
    },
    memberInfo: {
      margin: theme.spacing(2, 0, 0, 0)
    },
    requestDate: {
      margin: theme.spacing(2, 0, 0, 0)
    },
    updated: {
      margin: theme.spacing(2, 0, 0, 0)
    },
    updatedBy: {
      margin: theme.spacing(0)
    },
  };

  // #endregion

  // #region Methods

  /*
 * Check if updated request dates contain priority period
 */
  const checkPriorityDates = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // URL parameters
    const params = new URLSearchParams();
    params.append('signupID', memberSignup.signupID.toString());
    params.append('arrivalDate', memberSignup.arrivalDate);
    params.append('departureDate', memberSignup.departureDate);

    const url = new URL(`${API_URL}/v1/requests/areupdatedatespriority`);
    url.search = params.toString();

    return await fetch(url.toString(), {
      method: 'GET'
      , cache: 'no-cache'
      , headers: headers
      , mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to check if updated dates are in a priority period');
        }
        return response.json()
      })
      .then((sexc: SignupResultStatus) => {
        return (sexc.statusCode === SignupExceptionEnum.RequestDatesInPriorityPeriod);
      })
      .catch(err => {
        return false;
      }
      );
  }

  /*
   * Update member data in parent member list
   */
  const save = async () => {

    setEnableSave(false);

    // Determine if the dates are w/in a priority period if max reached
    if (await checkPriorityDates()) {
      enqueueSnackbar('Request dates can not include a priority period since the maximum priority requests has been reached.', { variant: 'warning', autoHideDuration: 7500 });
    } else {
      props.handleUpdate(memberSignup);
    }

  }

  /*
   * Validate if all data is present
   */
  const validate = () => {

    if (
      ((memberSignup.arrivalDate !== NULL_DATETIME_STRING) && (memberSignup.departureDate !== NULL_DATETIME_STRING))
      && ((memberSignup.arrivalDate !== props.memberSignup.arrivalDate) || (memberSignup.departureDate !== props.memberSignup.departureDate))
    ) {
      setEnableSave(true);
    } else {
      setEnableSave(false);
    }
  }

  // #endregion

  // #region Event handlers

  /*
* Event handler for changes to arrival date
*/
  const handleArrivalDateChange = (date: Date | null) => {

    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    const formattedDate = date ? dateFormatted(date) : EMPTY_STRING;

    if (formattedDate) {

      // Arrival must be today or greater
      const arrivalDate: Date = new Date(formattedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (arrivalDate < today) {
        enqueueSnackbar(`Arrival date must be today or later`, { variant: 'warning' });
        return;
      }

      // Departure date exists
      if (memberSignup.departureDate) {
        // Check if arrival >= departure date
        const arrivalDate: Date = new Date(formattedDate);
        const departureDate: Date = new Date(memberSignup.departureDate);
        if (arrivalDate >= departureDate) {
          enqueueSnackbar(`Arrival date must be earlier than departure date`, { variant: 'warning' });
        } else {
          // Set new arrival date
          setMemberSignup({
            ...memberSignup
            , arrivalDate: formattedDate
          });
        }
      } else {
        // Departure date is empty, set +1
        const plusDate: Date = new Date(formattedDate);
        plusDate.setDate(plusDate.getDate() + 1);
        // Set new arrival and departure date
        setMemberSignup({
          ...memberSignup
          , arrivalDate: formattedDate
          , departureDate: plusDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
        });
      }
    } else {
      validate();
    }

  };

  /*
   * Event handler for changes to departure date
   */
  const handleDepartureDateChange = (date: Date | null) => {

    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    const formattedDate = date ? dateFormatted(date) : EMPTY_STRING;

    if (formattedDate) {
      const departureDate: Date = new Date(formattedDate);

      // Departure must be tomorrow or greater
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date();
      yesterday.setHours(0, 0, 0, 0);
      yesterday.setDate(yesterday.getDate() - 1);

      if ((departureDate < today)) {
        enqueueSnackbar(`Departure date must be today or later`, { variant: 'warning' });
        return;
      }

      // Arrival date exists
      if (memberSignup.arrivalDate) {
        // Check if arrival >= departure date
        const departureDate: Date = new Date(formattedDate);
        const arrivalDate: Date = new Date(memberSignup.arrivalDate);
        if (departureDate <= arrivalDate) {
          enqueueSnackbar(`Departure date must be later than arrival date`, { variant: 'warning' });
        } else {
          // Set new departure date
          setMemberSignup({
            ...memberSignup
            , departureDate: formattedDate
          });
        }
      } else {
        // Arrival date is empty, set -1
        const minusDate: Date = new Date(formattedDate);
        minusDate.setDate(minusDate.getDate() - 1);
        setMemberSignup({
          ...memberSignup
          , arrivalDate: minusDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
          , departureDate: formattedDate
        });
      }
    } else {
      validate();
    }

  };

  React.useEffect(() => {
    // Load membrer data
    setMemberSignup({ ...props.memberSignup });
  }, [props.memberSignup]);

  React.useEffect(() => {
    if (memberSignup.memberSignupID !== NULL_ID) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSignup]);

  React.useEffect(() => {
    if (memberSignup.arrivalDate) {
      setMaxDeparture(calcMaxDeparture(memberSignup.arrivalDate));
      setMinDeparture(calcMinDeparture(memberSignup.arrivalDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSignup.arrivalDate]);

  React.useEffect(() => {
    if (memberSignup.departureDate) {
      setMaxArrival(calcMaxArrival(memberSignup.departureDate));
      setMinArrival(calcMinArrival(memberSignup.departureDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSignup.departureDate]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container alignItems="center">
        <Grid>
          <IconButton onClick={props.handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid>
          <Typography variant="h6">Member</Typography>
        </Grid>
      </Grid>
      <Grid container sx={styles.memberInfo}>
        <Typography variant="body1">{`${memberSignup.firstName} ${memberSignup.lastName}`}</Typography>
      </Grid>
      <Grid container>
        <Typography variant="overline">{memberSignup.memberStatus}</Typography>
      </Grid>
      <Grid container sx={styles.entryField}>
        <DatePicker
          disablePast
          format="MM/dd/yyyy"
          label="Arrival"
          minDate={minArrival ? new Date(minArrival) : undefined}
          maxDate={maxArrival ? new Date(maxArrival) : undefined}
          onChange={handleArrivalDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          value={memberSignup.arrivalDate === EMPTY_STRING ? null : new Date(memberSignup.arrivalDate)}
          views={['month', 'day']}
        />
      </Grid>
      <Grid container sx={styles.entryField}>
        <DatePicker
          disablePast
          format="MM/dd/yyyy"
          label="Departure"
          minDate={minDeparture ? new Date(minDeparture) : undefined}
          maxDate={maxDeparture ? new Date(maxDeparture) : undefined}
          onChange={handleDepartureDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          value={memberSignup.departureDate === EMPTY_STRING ? null : new Date(memberSignup.departureDate)}
          views={['month', 'day']}
        />
      </Grid>
      <Grid container justifyContent="center" sx={styles.actions}>
        <Tooltip title="Add or update member in request">
          <span>
            <Button variant="contained" color="primary" disabled={!enableSave} onClick={save} >Save</Button>
          </span>
        </Tooltip>
      </Grid>
      <Divider />
      <Grid container sx={styles.updated}>
        <Typography variant="overline">{`Updated: ${dateStringToLocalDateFormatted(memberSignup.updated)}`}</Typography>
      </Grid>
      <Grid container sx={styles.updatedBy}>
        <Typography variant="overline">{`By ${memberSignup.updatedBy}`}</Typography>
      </Grid>
      <Grid container sx={styles.requestDate}>
        <Typography variant="overline">{`Requested: ${dateStringToLocalDateFormatted(memberSignup.signupDate)}`}</Typography>
      </Grid>
    </Box>
  );

}

export default MemberRequestEdit;